/**
 * XState 5 call machine — one actor per call.
 * States: idle → (dialing | ringing) → active → ending → ended.
 * Impossible transitions are forbidden.
 *
 * NB: `dialing` heeft bewust GEEN `ANSWERED`-transitie. Het answered-event van
 * de credential-leg (browser-poot, auto-beantwoord ~3s na dial) zou het record
 * anders al actief maken terwijl de klant nog rinkelt — het echte outbound-
 * answer komt via Realtime (`MEDIA_FLOWING`) zodra de webhook de B-leg
 * gebridged heeft. Regressie-guard voor die bug.
 *
 * De machine-context is geminimaliseerd tot de lifecycle-velden die de machine
 * zélf muteert (single-writer-invariant). Verrijking (callId, customer, brand,
 * remoteNumber, …) leeft uitsluitend in `entry.record` van de registry en mag
 * hier nooit doorheen stromen — de oude volledige-CallRecord-context clobberde
 * die velden bij elke transitie terug naar hun initiële waarde.
 */

import { setup, assign, createActor, type Actor } from 'xstate';
import type { CallDirection, CallRecord, CallState } from './types';

export interface CallLifecycleContext {
  direction: CallDirection;
  state: CallState;
  answeredAt: Date | null;
  endedAt: Date | null;
  recoveredFromCallId: string | null;
}

export type CallMachineEvent =
  | { type: 'DIAL_REQUESTED' }
  | { type: 'RING_RECEIVED' }
  | { type: 'REMOTE_ANSWERED' }
  | { type: 'ANSWERED' }
  | { type: 'MEDIA_FLOWING' }
  | { type: 'HANGUP_REQUESTED' }
  | { type: 'DESTROYED'; reason?: string }
  | { type: 'RECOVERED'; fromId: string };

export const callMachine = setup({
  types: {} as {
    context: CallLifecycleContext;
    events: CallMachineEvent;
    input: CallLifecycleContext;
  },
  guards: {
    isOutbound: ({ context }) => context.direction === 'outbound',
    isInbound: ({ context }) => context.direction === 'inbound',
  },
  actions: {
    markAnswered: assign({
      answeredAt: ({ context }) => context.answeredAt ?? new Date(),
      state: () => 'active' as const,
    }),
    markEnded: assign({
      endedAt: () => new Date(),
      state: () => 'ended' as const,
    }),
    setDialing: assign({ state: () => 'dialing' as const }),
    setRinging: assign({ state: () => 'ringing' as const }),
    setEnding: assign({ state: () => 'ending' as const }),
    setRecovered: assign({
      recoveredFromCallId: ({ event }) =>
        event.type === 'RECOVERED' ? event.fromId : null,
    }),
  },
}).createMachine({
  id: 'call',
  context: ({ input }) => input,
  initial: 'idle',
  states: {
    idle: {
      on: {
        DIAL_REQUESTED: {
          target: 'dialing',
          guard: 'isOutbound',
          actions: 'setDialing',
        },
        RING_RECEIVED: {
          target: 'ringing',
          guard: 'isInbound',
          actions: 'setRinging',
        },
      },
    },
    dialing: {
      on: {
        REMOTE_ANSWERED: { target: 'active', actions: 'markAnswered' },
        MEDIA_FLOWING: { target: 'active', actions: 'markAnswered' },
        DESTROYED: { target: 'ended', actions: 'markEnded' },
        HANGUP_REQUESTED: { target: 'ending', actions: 'setEnding' },
      },
    },
    ringing: {
      on: {
        ANSWERED: { target: 'active', actions: 'markAnswered' },
        MEDIA_FLOWING: { target: 'active', actions: 'markAnswered' },
        DESTROYED: { target: 'ended', actions: 'markEnded' },
        HANGUP_REQUESTED: { target: 'ending', actions: 'setEnding' },
      },
    },
    active: {
      on: {
        DESTROYED: { target: 'ended', actions: 'markEnded' },
        HANGUP_REQUESTED: { target: 'ending', actions: 'setEnding' },
        RECOVERED: { actions: 'setRecovered' },
      },
    },
    ending: {
      on: {
        DESTROYED: { target: 'ended', actions: 'markEnded' },
      },
      after: {
        // Vangnet: blijft de DESTROYED van de SDK/Realtime uit (bv. server-
        // cancel zonder browser-leg), dan eindigt het gesprek alsnog en ruimt
        // de done-subscription in call-registry het record op (geen zombie).
        5000: { target: 'ended', actions: 'markEnded' },
      },
    },
    ended: { type: 'final' },
  },
});

export type CallActor = Actor<typeof callMachine>;

export function spawnCallActor(record: CallRecord): CallActor {
  const actor = createActor(callMachine, {
    input: {
      direction: record.direction,
      state: record.state,
      answeredAt: record.answeredAt,
      endedAt: record.endedAt,
      recoveredFromCallId: record.recoveredFromCallId,
    },
  });
  actor.start();
  return actor;
}
